/**
 * Le journal d'une note, de la touche au haut-parleur.
 *
 * # Ce qu'on cherche à voir
 *
 * Une note en séance traverse quatre instants, et aucun n'est observable depuis
 * un seul point du code : elle est **captée** à un tick, **émise** sur le
 * réseau, **reçue** chez le partenaire, puis **programmée** à un instant audio
 * qui peut ne pas être celui qu'elle visait. Tout ce qui s'entend de travers —
 * une note en retard, une durée qui n'est pas celle qu'on a jouée, un rythme
 * qui accroche — se lit dans l'écart entre ces quatre instants.
 *
 * # Le référentiel, et pourquoi ce n'est pas l'heure
 *
 * On pourrait tout horodater en `Date.now()` et se fier à l'heure du système :
 * deux appareils Apple sont synchronisés par NTP, à quelques millisecondes ou
 * quelques dizaines près. C'est trop grossier pour ce qu'on mesure, et c'est
 * inutile — il existe deux grandeurs *exactement* comparables entre les deux
 * machines :
 *
 *   - le **tick**, la position de la note sur la grille partagée. C'est lui qui
 *     sert de clé de jointure entre les deux journaux : la même note porte le
 *     même tick chez l'émetteur et chez le récepteur, au bit près.
 *   - la **position de séance** en millisecondes (`posMs`), qui découle de la
 *     même origine imposée par le service de temps. Elle est juste à l'erreur
 *     d'alignement d'horloges près, soit ~1 ms — dix à cinquante fois mieux que
 *     l'heure du système.
 *
 * `wallMs` est quand même relevé, parce qu'il coûte un appel et qu'il permet de
 * vérifier que les deux journaux parlent bien du même moment.
 *
 * # Ce que le relevé ne doit pas faire
 *
 * Peser. Il est **éteint par défaut** et les appelants sont gardés : quand la
 * trace est coupée, aucun de ces nombres n'est même calculé. Une mesure qui
 * change ce qu'elle mesure ne vaut rien, et c'est le fil principal — celui qui
 * programme les notes — qu'on instrumente.
 */

/** Ce qui est arrivé à un événement, à un endroit du parcours. */
export type NoteTraceRole =
  /** Confié au réseau par cette machine. */
  | "sent"
  /** Décodé à l'arrivée d'une trame. */
  | "recv"
  /** Remis au synthé, avec l'instant visé et l'instant retenu. */
  | "play";

export interface NoteTraceEntry {
  /** Ordre d'enregistrement. Deux entrées peuvent partager la milliseconde. */
  seq: number;
  role: NoteTraceRole;
  /** `"moi"` pour notre propre jeu, l'identifiant du pair sinon. */
  lane: string;
  kind: "noteOn" | "noteOff" | "sustain";
  /** Note MIDI, ou `-1` pour la pédale. */
  midi: number;
  velocity?: number;
  /** Position visée sur la grille partagée. La clé de jointure entre journaux. */
  tick: number;
  /** `Date.now()`, pour recoller deux journaux à la grosse maille. */
  wallMs: number;
  /** Position de séance au moment du relevé. Comparable entre machines. */
  posMs?: number;
  /** `play` : où le tick tombe sur la grille, en ms de séance. */
  targetMs?: number;
  /** `play` : où le son est réellement programmé, en ms de séance. */
  playedMs?: number;
  /**
   * `play` : l'instant visé était déjà passé, la note part au plus tôt.
   *
   * C'est le compteur qui tranche entre les deux causes d'irrégularité — une
   * conversion qui bouge, ou un budget trop court. On le cherchait depuis
   * longtemps.
   *
   * Sans délai de restitution, c'est le régime normal : le tick d'un geste
   * *est* l'instant présent. Avec un délai, c'est une anomalie sur toute lane
   * — le tampon a été dépassé — et `noteTraceSummary` en donne le taux par
   * lane dans `asapPercent`.
   */
  asap?: boolean;
  /** `recv` : horodatage de trame de l'émetteur, ms depuis *son* origine. */
  sentAt?: number;
}

/**
 * Taille de l'anneau.
 *
 * Deux mille entrées couvrent quelques minutes de jeu à deux, ce qui est la
 * durée d'une observation utile. Au-delà on écrase le plus ancien : ce qu'on
 * regarde est toujours ce qui vient de se passer.
 */
const CAPACITY = 2000;

const STORAGE_KEY = "virtuosa.trace.notes";

let enabled = false;
let restored = false;
let seq = 0;
const entries: NoteTraceEntry[] = [];

/**
 * Vrai quand le relevé est demandé.
 *
 * Lu par les appelants **avant** de calculer quoi que ce soit. Le drapeau est
 * repris de `localStorage` au premier appel, ce qui permet de l'armer puis de
 * recharger la page — indispensable pour observer une jointure de séance, qui
 * a lieu avant qu'on ait pu ouvrir une console.
 */
export function isNoteTraceEnabled(): boolean {
  if (!restored) {
    restored = true;

    try {
      enabled = localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      // Mode privé, cookies refusés : la trace reste éteinte, sans bruit.
    }
  }

  return enabled;
}

export function setNoteTraceEnabled(next: boolean): void {
  restored = true;
  enabled = next;

  try {
    if (next) {
      localStorage.setItem(STORAGE_KEY, "1");
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Le drapeau en mémoire suffit pour la session en cours.
  }
}

/** Enregistre une entrée. Sans effet quand la trace est éteinte. */
export function traceNote(entry: Omit<NoteTraceEntry, "seq" | "wallMs">): void {
  if (!isNoteTraceEnabled()) {
    return;
  }

  seq += 1;
  entries.push({ ...entry, seq, wallMs: Date.now() });

  if (entries.length > CAPACITY) {
    entries.shift();
  }
}

export function noteTraceEntries(): readonly NoteTraceEntry[] {
  return entries;
}

/**
 * Combien de fois le tampon de restitution a été dépassé.
 *
 * # Ce que ça compte
 *
 * Une note de partenaire est arrivée **après** son tick alors qu'un délai de
 * restitution était en vigueur : le trajet, ou un pic de gigue, a dépassé le
 * tampon. Elle est partie « au plus tôt » — jouer en retard vaut mieux que ne
 * pas jouer — mais elle a sonné hors de la grille, et c'est ce qui s'entend
 * comme un jeu qui accroche.
 *
 * # Pourquoi un compteur à côté du journal
 *
 * Le journal, lui, porte le drapeau `asap` par événement et sait dire d'où
 * vient chaque irrégularité ; c'est lui qu'on lit pour comprendre. Mais il est
 * éteint par défaut, et le dépassement doit se voir sans l'armer — c'est une
 * alarme, pas une mesure. Un entier qu'on incrémente ne pèse rien sur le fil
 * qui programme les notes, là où un `console.warn` par note noierait la
 * console au moment le plus chargé.
 */
let playoutOverruns = 0;

export function countPlayoutOverrun(): void {
  playoutOverruns += 1;
}

export function playoutOverrunCount(): number {
  return playoutOverruns;
}

export function resetPlayoutOverruns(): void {
  playoutOverruns = 0;
}

export function clearNoteTrace(): void {
  entries.length = 0;
  seq = 0;
}

/** Une note reconstituée, de son enfoncement à son relâchement. */
export interface NoteTracePair {
  lane: string;
  midi: number;
  tickOn: number;
  tickOff: number;
  /** Durée voulue : l'écart des deux ticks, porté sur la grille. */
  plannedMs: number;
  /** Durée entendue : l'écart des deux instants réellement programmés. */
  soundedMs: number;
  /** `soundedMs - plannedMs`. Positif : la note a duré plus que voulu. */
  durationErrorMs: number;
  /** Retard de l'attaque sur son instant visé. */
  lateOnMs: number;
  /** Retard du relâchement sur son instant visé. */
  lateOffMs: number;
  /** L'un des deux bouts est parti au plus tôt, faute d'avance. */
  asap: boolean;
}

/**
 * Apparie les enfoncements et les relâchements du journal.
 *
 * # Pourquoi la durée compte autant que l'attaque
 *
 * Un retard commun à toutes les notes ne s'entend pas comme un retard : il
 * s'entend comme rien du tout. Ce qui s'entend, c'est la **dispersion** — une
 * note qui arrive à côté de ses voisines — et la **déformation de durée**,
 * quand l'attaque et le relâchement ne subissent pas le même sort. C'est le cas
 * dès qu'un des deux bouts part « au plus tôt » : la longueur entendue devient
 * l'écart des arrivées, plus celui des instants joués.
 *
 * L'appariement se fait par touche, dans l'ordre : un `noteOff` ferme le
 * dernier `noteOn` encore ouvert sur cette touche et cette lane. Une note
 * encore tenue à la fin du journal n'apparaît pas — elle n'a pas de durée.
 */
export function noteTracePairs(
  source: readonly NoteTraceEntry[] = entries,
): NoteTracePair[] {
  const open = new Map<string, NoteTraceEntry>();
  const pairs: NoteTracePair[] = [];

  for (const entry of source) {
    if (entry.role !== "play" || entry.kind === "sustain") {
      continue;
    }

    const key = `${entry.lane}|${entry.midi}`;

    if (entry.kind === "noteOn") {
      open.set(key, entry);

      continue;
    }

    const start = open.get(key);

    if (start === undefined) {
      continue;
    }

    open.delete(key);

    const plannedMs = (entry.targetMs ?? 0) - (start.targetMs ?? 0);
    const soundedMs = (entry.playedMs ?? 0) - (start.playedMs ?? 0);

    pairs.push({
      lane: entry.lane,
      midi: entry.midi,
      tickOn: start.tick,
      tickOff: entry.tick,
      plannedMs,
      soundedMs,
      durationErrorMs: soundedMs - plannedMs,
      lateOnMs: (start.playedMs ?? 0) - (start.targetMs ?? 0),
      lateOffMs: (entry.playedMs ?? 0) - (entry.targetMs ?? 0),
      asap: (start.asap ?? false) || (entry.asap ?? false),
    });
  }

  return pairs;
}

export interface NoteTraceSummary {
  lane: string;
  /** Notes appariées. */
  notes: number;
  /**
   * Part de celles dont un bout est parti au plus tôt, en pourcentage.
   *
   * Avec un délai de restitution en vigueur, c'est le taux de dépassement du
   * tampon : la part des notes qui ont sonné hors de la grille.
   */
  asapPercent: number;
  /** Retard d'attaque : médiane et 95ᵉ centile, en ms. */
  lateOnMedianMs: number;
  lateOnP95Ms: number;
  /**
   * Dispersion des retards d'attaque, en ms — l'écart entre le 95ᵉ centile et
   * la médiane.
   *
   * C'est **la** grandeur qui décrit ce qui s'entend. Un retard régulier de
   * 40 ms est inaudible ; 40 ms qui varient de 15 ms d'une note à l'autre
   * s'entendent comme un jeu qui accroche.
   */
  lateOnSpreadMs: number;
  /** Erreur de durée : médiane et 95ᵉ centile de sa valeur absolue, en ms. */
  durationErrorMedianMs: number;
  durationErrorP95Ms: number;
}

/**
 * Résume le journal, une ligne par lane.
 *
 * C'est ce qu'il faut lire en premier, et souvent le seul tableau nécessaire :
 * la dispersion des retards dit si le problème s'entend, le taux de « au plus
 * tôt » dit d'où il vient, et l'erreur de durée dit si les relâchements
 * subissent autre chose que les attaques.
 */
export function noteTraceSummary(
  source: readonly NoteTraceEntry[] = entries,
): NoteTraceSummary[] {
  const byLane = new Map<string, NoteTracePair[]>();

  for (const pair of noteTracePairs(source)) {
    const bucket = byLane.get(pair.lane) ?? [];
    bucket.push(pair);
    byLane.set(pair.lane, bucket);
  }

  const summaries: NoteTraceSummary[] = [];

  for (const [lane, pairs] of byLane) {
    const lateOn = pairs.map((pair) => pair.lateOnMs);
    const errors = pairs.map((pair) => Math.abs(pair.durationErrorMs));
    const median = percentile(lateOn, 50);
    const p95 = percentile(lateOn, 95);

    summaries.push({
      lane,
      notes: pairs.length,
      asapPercent: round(
        (pairs.filter((pair) => pair.asap).length / pairs.length) * 100,
      ),
      lateOnMedianMs: round(median),
      lateOnP95Ms: round(p95),
      lateOnSpreadMs: round(p95 - median),
      durationErrorMedianMs: round(percentile(errors, 50)),
      durationErrorP95Ms: round(percentile(errors, 95)),
    });
  }

  return summaries;
}

/**
 * Le journal en CSV, prêt à coller ailleurs.
 *
 * Parce que la console d'un iPad se lit depuis un Mac et se copie mal : une
 * chaîne se sélectionne d'un coup, un tableau d'objets non.
 */
export function noteTraceCsv(source: readonly NoteTraceEntry[] = entries): string {
  const header = [
    "seq",
    "role",
    "lane",
    "kind",
    "midi",
    "velocity",
    "tick",
    "wallMs",
    "posMs",
    "targetMs",
    "playedMs",
    "asap",
    "sentAt",
  ];

  const lines = source.map((entry) =>
    [
      entry.seq,
      entry.role,
      entry.lane,
      entry.kind,
      entry.midi,
      entry.velocity ?? "",
      entry.tick,
      entry.wallMs,
      fixed(entry.posMs),
      fixed(entry.targetMs),
      fixed(entry.playedMs),
      entry.asap === undefined ? "" : entry.asap ? 1 : 0,
      entry.sentAt ?? "",
    ].join(","),
  );

  return [header.join(","), ...lines].join("\n");
}

function fixed(value: number | undefined): string {
  return value === undefined ? "" : value.toFixed(3);
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Centile par interpolation linéaire. Rend 0 sur une série vide. */
function percentile(values: readonly number[], rank: number): number {
  if (values.length === 0) {
    return 0;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const position = ((sorted.length - 1) * rank) / 100;
  const low = Math.floor(position);
  const high = Math.ceil(position);

  if (low === high) {
    return sorted[low];
  }

  return sorted[low] + (sorted[high] - sorted[low]) * (position - low);
}
